import {
  CameraFilled,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Modal } from "antd";
import { IconCorporateFareBlue } from "assets/svg";
import { getPublicAssetUrl } from "components/productSellerCompanyCard/productSellerCompanyCard.utils";
import React, { ChangeEvent, useRef, useState } from "react";

const DEFAULT_AVATARS = [
  "/avatar/seal.png",
  "/avatar/dolphin.png",
  "/avatar/penguin.png",
  "/avatar/person.png",
  "/avatar/polar-bear.png",
] as const;

interface CompanyInfoProps {
  companyName?: string;
  logoUrl?: string | null;
  canEditAvatar?: boolean;
  isAvatarBusy?: boolean;
  onAvatarUpload: (file: File) => Promise<void>;
  isLoading: boolean;
  isError: boolean;
  level?: number;
}

const CompanyInfo = ({
  companyName,
  logoUrl,
  canEditAvatar = false,
  isAvatarBusy = false,
  onAvatarUpload,
  isLoading,
  isError,
  level,
}: CompanyInfoProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string>();
  const [isConfirmingAvatar, setIsConfirmingAvatar] = useState(false);
  const isBusy = isAvatarBusy || isConfirmingAvatar;

  const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    void onAvatarUpload(file).catch(() => undefined);
    event.target.value = "";
  };

  const closeAvatarModal = () => {
    setIsAvatarModalOpen(false);
    setSelectedAvatar(undefined);
  };

  const openFilePicker = () => {
    closeAvatarModal();
    fileInputRef.current?.click();
  };

  const confirmAvatar = async () => {
    if (!selectedAvatar) return;
    setIsConfirmingAvatar(true);
    try {
      const response = await fetch(selectedAvatar);
      if (!response.ok) throw new Error("Failed to load avatar");
      const blob = await response.blob();
      await onAvatarUpload(
        new File(
          [blob],
          selectedAvatar.slice(selectedAvatar.lastIndexOf("/") + 1),
          { type: blob.type },
        ),
      );
      closeAvatarModal();
    } catch {
      return;
    } finally {
      setIsConfirmingAvatar(false);
    }
  };

  return (
    <section className="flex min-h-[120px] items-center gap-[20px]">
      <div className="relative h-[96px] w-[96px] shrink-0 md:h-[120px] md:w-[120px]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-[#d9e2ea] bg-[#eef5fa] text-[22px] font-bold text-white md:text-[24px]">
          {logoUrl ? (
            <img
              src={getPublicAssetUrl(logoUrl)}
              alt="company avatar"
              className="h-full w-full object-cover"
            />
          ) : (
            <IconCorporateFareBlue
              aria-hidden="true"
              className="h-[44px] w-[44px] text-[#1b4b86] md:h-[52px] md:w-[52px]"
            />
          )}
        </div>
        {canEditAvatar && (
          <>
            <input
              ref={fileInputRef}
              aria-label="プロフィール画像ファイル"
              type="file"
              accept="image/*"
              className="hidden"
              disabled={isBusy}
              onChange={handleAvatarChange}
            />
            <button
              type="button"
              aria-label="プロフィール画像を変更"
              className="absolute bottom-[4px] right-[4px] flex h-[34px] w-[34px] items-center justify-center rounded-full bg-[#4b4f52] text-[17px] text-white shadow-[0_2px_8px_rgba(0,0,0,0.25)] transition hover:bg-[#323639] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isBusy}
              onClick={() => setIsAvatarModalOpen(true)}
            >
              <CameraFilled />
            </button>
          </>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-[15px]">
          <h1 className="truncate text-[20px] font-bold leading-[1.6] text-black md:text-[24px]">
            {isLoading
              ? "読み込み中..."
              : isError
                ? "企業情報を取得できませんでした"
                : companyName || "-"}
          </h1>
          {typeof level === "number" && (
            <span className="bg-[linear-gradient(90deg,#d4f754_0%,#2cdcec_100%)] px-[10px] py-[3px] text-[14px] font-bold leading-[1.2] text-white md:text-[16px]">
              Lv.{level}
            </span>
          )}
        </div>
        <p className="mt-[3px] text-[14px] leading-[1.6] text-black">
          企業アカウント
        </p>
      </div>
      <Modal
        title="プロフィール画像を選択"
        open={isAvatarModalOpen}
        onCancel={closeAvatarModal}
        footer={null}
        destroyOnHidden
      >
        <div className="grid grid-cols-3 gap-2 py-4 sm:grid-cols-5">
          {DEFAULT_AVATARS.map((avatar, index) => (
            <button
              key={avatar}
              type="button"
              aria-label={`アバター${index + 1}`}
              aria-pressed={selectedAvatar === avatar}
              disabled={isBusy}
              className={`flex h-full items-center justify-center rounded-lg border-2 p-1 ${
                selectedAvatar === avatar
                  ? "border-[#1b4b86]"
                  : "border-transparent"
              }`}
              onClick={() => setSelectedAvatar(avatar)}
            >
              <img
                src={avatar}
                alt=""
                className="h-[88px] w-[88px] shrink-0 object-contain"
              />
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="rounded-md border border-[#1b4b86] px-4 py-2 font-bold text-[#1b4b86]"
            disabled={isBusy}
            onClick={openFilePicker}
          >
            <UploadOutlined className="mr-2" />
            端末からアップロード
          </button>
          <button
            type="button"
            className="rounded-md bg-[#1b4b86] px-5 py-2 font-bold text-white disabled:opacity-50"
            aria-busy={isConfirmingAvatar}
            disabled={!selectedAvatar || isBusy}
            onClick={confirmAvatar}
          >
            {isConfirmingAvatar && <LoadingOutlined spin className="mr-2" />}
            確定
          </button>
        </div>
      </Modal>
    </section>
  );
};

export default CompanyInfo;
